import React, { useEffect, useState } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { countryOptions } from './phoneCountries.ts';

interface PhoneNumberInputProps {
  value: {
    code: string;
    number: string;
  };
  onChange: (val: { code: string; number: string }) => void;
}

const normalizeCode = (input: string): string => {
  if (!input) return '';
  if (input.startsWith('00')) return '+' + input.slice(2);
  if (input.startsWith('+')) return input;
  return '+' + input;
};

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({ value, onChange }) => {
  const [localError, setLocalError] = useState(false);
  const [helperText, setHelperText] = useState('');

  const detectAndSplitNumber = (input: string) => {
    const sanitizedInput = input.replace(/\s+/g, '').replace(/[^0-9+]/g, '');

    // Special case: 12-digit Indian number like 9190xxxxxx
    if (/^91\d{10}$/.test(sanitizedInput)) {
      return {
        code: '+91',
        number: sanitizedInput.slice(2),
      };
    }

    // Match +91, 0091, 091 etc
    const sortedCodes = [...countryOptions].sort((a, b) => b.value.length - a.value.length);

    for (const country of sortedCodes) {
      const normalized = normalizeCode(country.value.replace('+', ''));
      const patterns = [
        `^\\+${normalized.slice(1)}`,
        `^00${normalized.slice(1)}`,
        `^0${normalized.slice(1)}`,
      ];

      for (const pattern of patterns) {
        const regex = new RegExp(pattern);
        if (regex.test(sanitizedInput)) {
          return {
            code: country.value,
            number: sanitizedInput.replace(regex, '').trim(),
          };
        }
      }
    }

    // Default fallback
    return {
      code: value.code || '+91',
      number: sanitizedInput,
    };
  };

  const validate = (number: string) => {
    const digitsOnly = number.replace(/\D/g, '');
    // const digitsOnly = typeof number === 'string' ? number.replace(/\D/g, '') : '';

    const isValid = /^\d{10}$/.test(digitsOnly);
    setLocalError(!isValid);
    setHelperText(
      !isValid && digitsOnly.length > 0 ? 'Please enter a valid 10-digit mobile number' : ''
    );
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.trim();
    const parsed = detectAndSplitNumber(input);
    const digitsOnly = input.replace(/\D/g, '');
    if (digitsOnly.length > 13) {
      return;
    }
    onChange(parsed);
    validate(parsed.number);
  };

  const handleCountryChange = (selected: any) => {
    if (selected) {
      onChange({ ...value, code: selected.value });
    }
  };

  useEffect(() => {
    if (!value.code) {
      onChange({ ...value, code: '+91' });
    }
  }, []);

  useEffect(() => {
    if (value?.number) {
      validate(value.number);
    }
  }, [value?.number]);

  const selectedOption = countryOptions.find((option) => option.value === value.code);

  return (
    <Box width="100%">
      <TextField
        label="Phone Number"
        fullWidth
        size="small"
        value={value.number}
        onChange={handleNumberChange}
        error={localError}
        helperText={helperText}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ width: 100, mr: 1 }}>
              <SearchableDropdown
                options={countryOptions}
                value={selectedOption || null}
                onChange={handleCountryChange}
                labelKey="label"
                valueKey="value"
                label={null}
                size="small"
                inputProps={{
                  maxLength: 13,
                  inputMode: 'numeric', // optional for better mobile keyboard
                }}
                sx={{
                  minWidth: 100,
                  '& .MuiAutocomplete-inputRoot': {
                    padding: '2px 6px',
                    fontSize: '0.8rem',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
                getOptionLabel={(option) => option.label}
                ListboxProps={{
                  style: {
                    maxHeight: '20vh',
                  },
                }}
              />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default PhoneNumberInput;
